import React from 'react';
import { twMerge } from 'tailwind-merge';
import { CircleRadioBoxIcon } from '@/icons/CircleRadioBoxIcon';
import { CircleCheckBoxIcon } from '@/icons/CircleCheckBoxIcon';
import { SwitchButton as SwitchButtonComponent, SwitchButtonSize } from '@/ui-components/common/SwitchButton';

export type FilterItem = {
  title: string;
  value?: any;
  checked?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

export type FilterMenuGroup = {
  key: string;
  type: FilterMenuType;
  items: FilterItem[];
  description?: string;
};

export enum FilterMenuType {
  CircleRadioButton = 'CircleRadioButton',
  CircleCheckBoxButton = 'CircleCheckBoxButton',
  SwitchButton = 'SwitchButton',
}

interface FilterItemListProps {
  className?: string;
  filterMenuGroups: FilterMenuGroup[];
  onClick?: () => void;
}

const FilterMenuItem = ({
  item,
  type,
  description,
  divider,
}: {
  item: FilterItem;
  type: FilterMenuGroup['type'];
  description?: string;
  divider?: boolean;
}) => {
  const renderIcon = (onClick?: () => void) => {
    switch (type) {
      case 'CircleRadioButton':
        return <CircleRadioBoxIcon checked={item.checked} />;
      case 'CircleCheckBoxButton':
        return <CircleCheckBoxIcon checked={item.checked} size={20} />;
      case 'SwitchButton':
        return (
          <SwitchButtonComponent
            size={SwitchButtonSize.small}
            checked={item.checked ?? false}
            id={item.title}
            onChange={onClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <li
      className={twMerge(
        'flex flex-col w-full items-center text-black',
        divider && 'border-gray-200 border-solid border-0 border-t-[1px]',
      )}>
      <div
        className={twMerge(
          'flex flex-row items-center gap-2 w-full px-4 py-2.5 whitespace-nowrap ',
          item.disabled ? 'opacity-30' : 'cursor-pointer text-black hover:bg-gray-200 active:bg-gray-200',
        )}
        onClick={() => !item.disabled && item.onClick?.()}>
        <div className="flex items-center gap-2 w-full text-left border-none">
          {renderIcon(item.onClick)}
          <p className="text-xxs font-regular">{item.title}</p>
        </div>
      </div>
      {description && item.checked && (
        <div className="flex items-center text-left text-xxxs font-regular text-gray-500 mb-[10px]"> {description}</div>
      )}
    </li>
  );
};

export const FilterItemList = ({ className, onClick, filterMenuGroups }: FilterItemListProps) => (
  <ul
    className={twMerge(
      'absolute top-[calc(100%+8px)] right-0 min-w-[calc(100%-16px)] py-2 bg-white border border-solid border-black/10 rounded overflow-hidden z-[999] shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)]',
      className,
    )}
    onClick={onClick}>
    {filterMenuGroups.map((group, groupIndex) => (
      <div key={group.key}>
        {group.items.map((item, itemIndex) => (
          <FilterMenuItem
            key={`${group.key}_${item.title}`}
            item={item}
            type={group.type}
            description={group.description}
            divider={groupIndex > 0 && itemIndex === 0}
          />
        ))}
      </div>
    ))}
  </ul>
);
